/**
 * One line of a comanda on a station card.
 *
 * Pressing the line opens the dish. Changing its state is the explicit button
 * on the right — a cook was changing statuses by touching the dish name.
 *
 * A line standing for several identical plates carries their count and a
 * control that opens it. Opened, each plate is an ordinary line again, which is
 * how anything done to ONE of them keeps working exactly as it does today.
 */
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { isOffMenuItem, itemDisplayName, OFF_MENU_TAG } from '../lib/off-menu';
import { useKitchenStore } from '../stores/kitchen.store';
import { STATUS_CYCLE, STATUS_DOT_CLASS, STATUS_LABEL } from '../lib/order-card';
import DishRowStatusButton from './DishRowStatusButton';
import DinerTagChip from './DinerTagChip';
import { useTranslation } from '../i18n';
import type { KdsT } from '../lib/i18n/settings';

interface OrderCardItemRowProps {
  item: any;
  compact?: boolean;
  isLoading: boolean;
  onAdvance: () => void;
  onOpenDish: (item: any) => void;
  /** How many plates this line stands for. Absent or 1 draws today's row. */
  count?: number;
  /** "2 di 3 pronti", when the plates of the line are not all at one status. */
  statusHint?: string | null;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  /** Feature 268 — the venue shows who each plate is for. */
  showDinerTag?: boolean;
}

function StockDot({ dishId }: { dishId?: string }) {
  const { t } = useTranslation();
  const dishInventory = useKitchenStore((s) => s.dishInventory);
  const stock = dishId ? dishInventory[dishId] : null;
  if (!stock) return null;
  if (stock.isOutOfStock) {
    return (
      <span
        className="inline-block w-3 h-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: '#EF4444' }}
        title={t('kds.stock.outOfStock', 'Esaurito (scorte: {{stock}})', { stock: stock.currentStock })}
      />
    );
  }
  if (stock.isLowStock) {
    return (
      <span
        className="inline-block w-3 h-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: '#F97316' }}
        title={t('kds.stock.low', 'Scorte basse ({{stock}} rimasti)', { stock: stock.currentStock })}
      />
    );
  }
  return null;
}

export function glutenFreeDetail(item: any, t: KdsT): string | null {
  const changes = item?.glutenFreeChanges;
  if (!changes) return null;
  const bits: string[] = [];
  for (const r of changes.replaced ?? []) bits.push(t('kds.glutenFree.replaced', '{{to}} al posto di {{from}}', { to: r.to, from: r.from }));
  if ((changes.omitted ?? []).length > 0) bits.push(t('kds.glutenFree.omitted', 'senza {{items}}', { items: changes.omitted.join(', ') }));
  return bits.length > 0 ? ` — ${bits.join(', ')}` : null;
}

export default function OrderCardItemRow({
  item,
  compact,
  isLoading,
  onAdvance,
  onOpenDish,
  count = 1,
  statusHint = null,
  expanded = false,
  onToggleExpanded,
  showDinerTag = false,
}: OrderCardItemRowProps) {
  const { t } = useTranslation();
  const label = STATUS_LABEL[item.status]?.(t) || item.status;

  return (
    <div
      onClick={() => onOpenDish(item)}
      className={[
        'flex items-center justify-between py-1.5 px-2.5 rounded-lg transition-colors cursor-pointer hover:brightness-110 active:scale-[0.99]',
        item.status === 'PREPARING'
          ? 'bg-kds-preparing/10 animate-preparing'
          : item.status === 'READY'
            ? 'bg-kds-ready/10'
            : item.status === 'SERVED'
              ? 'bg-kds-served/10 opacity-50'
              : 'bg-kds-card/50',
        isLoading ? 'opacity-60 cursor-wait' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      title={t('kds.dishRow.openDishHint', 'Tocca per vedere il piatto: foto e ingredienti')}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {count > 1 && <span className="text-kds-qty text-kds-text shrink-0">×{count}</span>}
          <span className="text-kds-dish text-kds-text">{itemDisplayName(item)}</span>
          {/* 248 — off the menu: the title is the waiter's text, this
              tag is all that is left of the anchor dish's name. */}
          {isOffMenuItem(item) && (
            <span
              className="text-kds-badge uppercase tracking-wider text-kds-muted bg-kds-muted/15 px-2 py-0.5 rounded shrink-0"
              title={t('kds.offMenu.hint', 'Piatto scritto dal cameriere al tavolo: la nota è tutta la ricetta')}
            >
              {OFF_MENU_TAG(t)}
            </span>
          )}
          <StockDot dishId={item.dish?.id} />
          {/* 268 — who it goes to. Nothing at all when the line carries no
              diner or the venue has the feature off. */}
          <DinerTagChip item={item} enabled={showDinerTag} />
        </div>

        {/* Loud on purpose: the cook must not have to look for it. */}
        {item.isGlutenFreeVersion && (
          <p className="text-kds-note mt-0.5 font-bold text-kds-preparing">
            {t('kds.dish.glutenFree', '⚠ SENZA GLUTINE')}
            {glutenFreeDetail(item, t)}
          </p>
        )}
        {item.modifications && (
          <p className="text-kds-note text-kds-preparing mt-0.5">&#x26A0; {item.modifications}</p>
        )}
        {item.notes && <p className="text-kds-note text-kds-muted mt-0.5">{item.notes}</p>}
        {item.movedFromTableLabel && (
          <p className="text-kds-note text-kds-muted mt-0.5">
            {t('kds.dishRow.movedFrom', '↪ spostato da {{table}}', { table: item.movedFromTableLabel })}
          </p>
        )}
        {statusHint && <p className="text-kds-note text-kds-muted mt-0.5">{statusHint}</p>}

        {onToggleExpanded && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
            className="mt-2 flex items-center gap-1 text-kds-meta text-kds-muted hover:text-kds-text min-h-touch"
          >
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            {expanded
              ? t('kds.dishRow.collapse', 'Richiudi')
              : t('kds.dishRow.openPortions', 'Apri le {{count}}', { count })}
          </button>
        )}
      </div>

      {!compact && (
        <div className="ml-2 flex items-center gap-2 shrink-0">
          <span
            className={`inline-block w-5 h-5 rounded-full ${
              STATUS_DOT_CLASS[item.status] || 'bg-kds-muted'
            }`}
            title={label}
          />
          <span className="text-kds-meta text-kds-muted hidden sm:inline">{label}</span>
          {item.status in STATUS_CYCLE && !isLoading && (
            <ChevronRight size={26} className="text-kds-muted opacity-60" />
          )}
          {isLoading && (
            <span className="w-6 h-6 border-2 border-kds-muted border-t-transparent rounded-full animate-spin" />
          )}
        </div>
      )}

      {compact && (
        <div className="ml-2 shrink-0" onClick={(e) => e.stopPropagation()}>
          <DishRowStatusButton
            item={item}
            isLoading={isLoading}
            label={label}
            onAdvance={onAdvance}
          />
        </div>
      )}
    </div>
  );
}
